/**
 * Palworld technology-tree entity database.
 *
 * Moved out of the route file so each unlock can own its own indexable page
 * (/technology-tree/<slug>) — those are the "palworld assault rifle unlock
 * level", "palworld jetragon saddle" style queries competitor wikis capture
 * with per-entity URLs.
 */
export type TechTier = "Tech" | "Ancient";
export type TechCat = "Base" | "Crafting" | "Combat" | "Mount" | "Utility" | "Farming";

export interface TechNode {
  slug: string;
  lvl: number;
  name: string;
  tier: TechTier;
  cost: number;
  cat: TechCat;
  needs?: string;
  note: string;
}

const slugify = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const N = (
  lvl: number,
  name: string,
  tier: TechTier,
  cost: number,
  cat: TechCat,
  note: string,
  needs?: string,
): TechNode => ({ slug: slugify(name), lvl, name, tier, cost, cat, needs, note });

// Curated set derived from the public in-game tech tree. Points and levels are
// canonical in-game values; prerequisites reflect the standard unlock chain.
export const TECH_NODES: TechNode[] = [
  N(2, "Primitive Workbench", "Tech", 1, "Crafting", "Craft weapons, tools and structures. Prereq for almost everything."),
  N(2, "Pal Sphere", "Tech", 1, "Utility", "Basic capture sphere. Required for any Pal catching progress."),
  N(3, "Wooden Club", "Tech", 1, "Combat", "Starter melee weapon; keep until Stone Spear."),
  N(4, "Campfire", "Tech", 1, "Base", "Cooks meat, boosts Kindling Pals early.", "Primitive Workbench"),
  N(6, "Bow", "Tech", 2, "Combat", "First ranged weapon. Pair with Arrows unlock."),
  N(7, "Pal Box", "Tech", 2, "Base", "Establishes a new base; you can own up to 3."),
  N(8, "Stone Pickaxe", "Tech", 1, "Utility", "Mines ore. Combine with a Mining Pal for throughput."),
  N(10, "Mega Sphere", "Tech", 2, "Utility", "Better catch rates for mid-tier Pals."),
  N(11, "Feed Box", "Tech", 2, "Base", "Feed base Pals; SAN drops without it.", "Pal Box"),
  N(12, "Nail", "Tech", 1, "Crafting", "Enables higher-tier structures and weapons."),
  N(13, "Handgun", "Tech", 3, "Combat", "Consumes Ammunition; strong mid-game DPS."),
  N(14, "Cooking Pot", "Tech", 2, "Base", "Recipes with buffs; core for base Pal SAN.", "Campfire"),
  N(15, "Giga Sphere", "Tech", 2, "Utility", "Stronger sphere; needed to catch Alpha Pals reliably."),
  N(16, "Ranch", "Tech", 2, "Farming", "Passive item production from Chikipi, Mozzarina, Vixy.", "Pal Box"),
  N(17, "Assembly Line", "Tech", 3, "Crafting", "Faster crafting when assigned Handiwork Pals.", "Primitive Workbench"),
  N(19, "Berry Plantation", "Tech", 2, "Farming", "First reliable food source for a big base."),
  N(21, "Hyper Sphere", "Tech", 3, "Utility", "Reliable catches into the mid-30s level range."),
  N(21, "Electric Furnace", "Tech", 3, "Crafting", "Smelts refined ingots; requires power."),
  N(22, "Grappling Gun", "Tech", 2, "Utility", "Traversal upgrade; huge quality-of-life boost."),
  N(24, "Assault Rifle", "Tech", 5, "Combat", "First full-auto weapon; tower boss killer."),
  N(25, "Wheat Plantation", "Tech", 3, "Farming", "Enables bread and higher-SAN meals.", "Berry Plantation"),
  N(28, "Ultra Sphere", "Tech", 3, "Utility", "High-tier sphere; strong on Sakurajima Pals."),
  N(29, "Refined Metal Ingot", "Tech", 3, "Crafting", "Gate for late-game weapons and armor.", "Electric Furnace"),
  N(33, "Pal Metal Armor", "Tech", 5, "Combat", "Best pre-legendary armor. Cold + Heat resist variants."),
  N(34, "Rocket Launcher", "Tech", 6, "Combat", "AoE for raid mobs and towers."),
  N(36, "Legendary Sphere", "Ancient", 5, "Utility", "Best possible sphere; effectively required to catch Jetragon."),
  N(40, "Pal Essence Condenser", "Ancient", 3, "Utility", "Star-rank Pals by feeding duplicates. Core endgame loop."),
  N(42, "Structural Condenser", "Ancient", 5, "Base", "Increases base build height; needed for tall factories."),
  N(43, "Missile Launcher", "Ancient", 6, "Combat", "Homing missiles; strong on flying raid bosses."),
  N(44, "Homing Sphere Launcher", "Ancient", 6, "Utility", "Fires spheres at range; makes flying Pal capture trivial."),
  N(46, "Legendary Assault Rifle", "Ancient", 7, "Combat", "Top-tier gun; needs Refined Ingots + Ancient Civ Parts."),
  N(47, "Legendary Shield", "Ancient", 5, "Combat", "Highest-HP shield; stacks with Pal Metal armor."),
  N(48, "Pal Gear Workbench", "Ancient", 4, "Crafting", "Crafts saddles for Jetragon, Frostallion, Necromus, Paladius."),
  N(49, "Jetragon Saddle", "Ancient", 6, "Mount", "Unlocks Palworld's fastest flying mount.", "Pal Gear Workbench"),
  N(50, "Legendary Rocket Launcher", "Ancient", 8, "Combat", "Top-tier AoE; melts raid bosses in <60s."),
  N(51, "Frostallion Saddle", "Ancient", 6, "Mount", "Ice legendary flyer; ignores heatstroke biomes.", "Pal Gear Workbench"),
  N(52, "Legendary Bow", "Ancient", 6, "Combat", "Highest-damage bow; silent boss cheese."),
  N(53, "Necromus Saddle", "Ancient", 6, "Mount", "Ground legendary; fastest non-flying mount.", "Pal Gear Workbench"),
  N(55, "Paladius Saddle", "Ancient", 6, "Mount", "Holy ground legendary; heavy CC on charge.", "Pal Gear Workbench"),
  N(60, "Ancient Civilization Parts Bench", "Ancient", 8, "Crafting", "Endgame crafting hub; converts parts to modules for legendary gear."),
];

export const TECH_CATS: TechCat[] = ["Base", "Crafting", "Combat", "Mount", "Utility", "Farming"];

export const TECH_BY_SLUG: Record<string, TechNode> = Object.fromEntries(
  TECH_NODES.map((n) => [n.slug, n]),
);
